import { getHelper } from '@/devtools/utils';
import {
  SIMPLE_CHOICE,
  SIMPLE_CHOICE_META,
} from '@/features/ChoicesLists/Simple/types';

import { ROLE, useClientStore } from '@/features/Clients';
import {
  VersicherungsnehmerCompanyPropertyContractSchema,
  VersicherungsnehmerPropertyContractSchema,
} from '@/features/Clients/Schemas/Versicherungsnehmer';
import type { ComparisonDetail } from '@/features/Comparison/Components/types';
import { SEARCH_DATA_CONTEXT } from '@/features/Global/types';
import { useHsnTsn } from '@/features/HsnTsn/useHsnTsn';
import { pkwContractDehydrationDefinitions } from '@/features/Sparten/pkw/contractDehydration';
import {
  isCompany,
  isNotHaftpflicht,
  isVollkasko,
} from '@/features/Sparten/shared/renderConditions/kfz/sharedRenderConditions';
import { pkwInputDehydrationDefinitions } from '@/features/Sparten/pkw/inputDehydration';
import { minderjaehrigeKinderSchema } from '@/features/Sparten/shared/schema/kfz/versicherungsnehmerSchema/versicherungsnehmerSchema';
import type { InputStore } from '@/features/Sparten/pkw/useInputStore';
import { usePkwInputStore } from '@/features/Sparten/pkw/useInputStore';
import { KASKOTYP } from '@/features/Sparten/shared/types';
import {
  filterCategories,
  filterItemsByMetaData,
} from '@/features/Sparten/shared/utils/featureFilter';
import { SpartenConfig } from '@/features/Sparten/types';
import { dehydrateRequestData } from '@/utils/dehydrateRequestData/dehydrateRequestData';
import boolean from '@/utils/formatters/boolean';
import { formatCurrency } from '@/utils/formatters/euro';
import { formatChoice } from '@/utils/formatters/simpleChoice';
import string from '@/utils/formatters/string';
import { getSimpleChoiceMeta } from '@/utils/getSimpleChoiceMeta/getSimpleChoiceMeta';
import { useStorableState } from '@/utils/useStorableState/useStorableState';
import {
  KEEP_DATA_FROM_QUOTE_MAPPING,
  keepDataFromQuoteMappings,
} from '@/features/Sparten/shared/keepDataFromQuoteMappings';
import {
  kfzAlternateTariffSearchRequestIdentifier,
  kfzInventoryContractTitle,
  kfzSpecifiedCompanyFilter,
} from '@/features/Sparten/shared/utils/kfzConfigUtils';
import { kfzPreviousInsurance } from '@/features/Sparten/shared/utils/hasPreviousInsurance';
import type { Company } from '@/features/Companies/types';
import { versicherungsnehmerGroupMapping } from '@/features/Sparten/pkw/inputMappings/versicherungsnehmerGroup';
import { carGroupMapping } from '@/features/Sparten/pkw/inputMappings/carGroup';
import { absicherungsGroupMapping } from '@/features/Sparten/pkw/inputMappings/absicherungsGroup';
import { beitragsreduzierungsGroupMapping } from '@/features/Sparten/pkw/inputMappings/beitragsreduzierungsGroup';
import { ENTITY_TYPE } from '@/features/EntityManager/types';
import { relevantPreviousInsuranceMissMatchTypes } from '@/features/Sparten/shared/kfz/relevantPreviousInsuranceMissMatchTypes';
import { usedFahrerRoles } from '@/features/Sparten/shared/components/kfz/Fahrerkreis/utils';
import {
  kfzFahrerMetaSchema,
  KfzFahrerResource,
} from '@/features/Sparten/shared/schema/kfz/clientSchema/clientSchema';
import { kfzInputClientEntityDehydrationDefinitions } from '@/features/Sparten/shared/clientDehydration/kfz/conditionalClientRequestDefinitions';
import { calculationRelevantFahrerFields } from '@/features/Sparten/shared/kfz/calculationRelevantFahrerFields';
import { keepDataFromQuoteClientConfiguration } from '@/features/Sparten/shared/kfz/keepDataFromQuoteClientConfiguration';
import image from './assets/bg.jpg';
import { pkwVersicherungsangabenConfig } from '@/features/Sparten/pkw/versicherungsangabenConfig';

export const config: SpartenConfig = {
  component: () => import('@/views/Base/Layout.vue'),
  path: '/pkw',
  props: {
    name: 'Autoversicherung',
    sparte: 'pkw',
    image,
  },

  general: {
    maxSimultaneousCount: -1,

    versicherungsangabenConfig: pkwVersicherungsangabenConfig,

    distribution: {
      channel: true,
    },

    clientFields: {
      versicherungsnehmer: {
        anredeId: 'anredeId',
        geburtsdatum: 'geburtsdatum',
        familienstand: 'familienstand',
        plz: 'adresse.plz',
        nationalitaet: 'nationalitaet',
        fuehrerscheindatum: 'fuehrerscheindatum',
      },
      abweichenderHalter: {
        plz: 'adresse.plz',
        ort: 'adresse.ort',
        geburtsdatum: 'geburtsdatum',
      },
    },

    calculationRelevantEntityFields: {
      [ENTITY_TYPE.CLIENT]: {
        versicherungsnehmer: [
          'anredeId',
          'geburtsdatum',
          'familienstand',
          'adresse.plz',
          'nationalitaet',
          'fuehrerscheindatum',
        ],
        abweichenderHalter: ['adresse.plz', 'adresse.ort', 'geburtsdatum'],
        fahrer1: calculationRelevantFahrerFields,
        fahrer2: calculationRelevantFahrerFields,
        fahrer3: calculationRelevantFahrerFields,
        fahrer4: calculationRelevantFahrerFields,
        fahrer5: calculationRelevantFahrerFields,
      },
    },

    profileFields: [
      'allgemein.versicherungsartSelection',
      'allgemein.versicherungsart',
      'fahrzeug.antriebsart',
      'fahrzeug.nutzungsart',
      'fahrzeug.auslandsnutzung',
      'versicherungsnehmer.isFirma',
      'kaskoTyp',
      'deckungssumme',
      'werkstattbindung',
      'tierbissfolgeschaeden',
      'fahrerschutzversicherung',
      'schutzbriefleistungen',
      'rabattschutzKfzHaftpflicht',
      'kollisionMitTieren',
      'gapDeckung',
      'kaskoSB.teilkasko',
      'kaskoSB.vollkasko',
      'kaskoSB.gleicheSB',
    ],

    isVersicherungsbeginnFromThePastAllowed: true,

    keepDataFromQuote: {
      clients: {
        ...keepDataFromQuoteClientConfiguration,
        abweichenderHalter: [
          ...keepDataFromQuoteMappings[KEEP_DATA_FROM_QUOTE_MAPPING.BIRTHDAY],
          ...keepDataFromQuoteMappings[KEEP_DATA_FROM_QUOTE_MAPPING.ADDRESS],
        ],
      },

      copyClientFieldsToInputStore: {
        'adresse.ort': 'versicherungsnehmer.ort',
      },
    },

    relevantPreviousInsuranceMissMatchTypes,

    clientEntityDehydration: {
      fahrer1: kfzInputClientEntityDehydrationDefinitions,
      fahrer2: kfzInputClientEntityDehydrationDefinitions,
      fahrer3: kfzInputClientEntityDehydrationDefinitions,
      fahrer4: kfzInputClientEntityDehydrationDefinitions,
      fahrer5: kfzInputClientEntityDehydrationDefinitions,
    },

    clientEntityMetaSchema: {
      fahrer1: kfzFahrerMetaSchema,
      fahrer2: kfzFahrerMetaSchema,
      fahrer3: kfzFahrerMetaSchema,
      fahrer4: kfzFahrerMetaSchema,
      fahrer5: kfzFahrerMetaSchema,
    },

    buildInputDataObject: (context: SEARCH_DATA_CONTEXT, dehydrate = false) => {
      const state = usePkwInputStore().$state as InputStore;

      const preparedData = useStorableState().create(state);

      if (context === SEARCH_DATA_CONTEXT.TARIFF && dehydrate) {
        return dehydrateRequestData(
          preparedData,
          pkwInputDehydrationDefinitions,
        );
      }

      return preparedData;
    },

    buildContractDataObject: (state: Record<string, unknown>) =>
      dehydrateRequestData(state, pkwContractDehydrationDefinitions),

    quotePreInputMergeCallback: (state: Record<string, unknown>) => {
      const pkwQuoteData = state as InputStore;
      // If we have an object, which only contains undefined key value pairs, its getting stripped to an empty array
      // New quotes (after this release) will have other defaults, but we need this snippet to fix broken offers on the fly

      if (
        Array.isArray(pkwQuoteData.versicherungsnehmer.minderjaehrigeKinder)
      ) {
        pkwQuoteData.versicherungsnehmer.minderjaehrigeKinder =
          minderjaehrigeKinderSchema.getDefault();
      }

      if (
        pkwQuoteData.versicherungsnehmer.wohneigentum !== undefined &&
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        pkwQuoteData.versicherungsnehmer.wohneigentum.gesellschaftID === ''
      ) {
        pkwQuoteData.versicherungsnehmer.wohneigentum.gesellschaftID = 0;
      }

      usedFahrerRoles.value.forEach((role) => {
        const driver =
          useClientStore().getClientByRole<KfzFahrerResource>(role);
        if (Array.isArray(driver.data.meta.minderjaehrigeKinder)) {
          driver.data.meta.minderjaehrigeKinder =
            minderjaehrigeKinderSchema.getDefault();
        }

        if (
          'wohneigentum' in driver.data.meta &&
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          driver.data.meta.wohneigentum.gesellschaftID === ''
        ) {
          driver.data.meta.wohneigentum.gesellschaftID = 0;
        }
      });

      return pkwQuoteData;
    },

    featureFilterFunction: () => {
      const filteredCategories = filterCategories();

      return filterItemsByMetaData(filteredCategories);
    },
    specifiedCompanyFilter: (): Company[] => kfzSpecifiedCompanyFilter(),

    postLoadingSessionState: () => {
      // We need to revalidate hsn & tsn since it could already have been loaded from a quote.
      // If we don`t revalidate, the modell name would refer to the original hsn/tsn combination
      // since we only patch the input store, but dont trigger the HsnInput.vue changes
      useHsnTsn().revalidateHsnTsnInformation(
        usePkwInputStore().fahrzeug.hsn,
        usePkwInputStore().fahrzeug.tsn,
      );
    },

    getAlternateTariffSearchRequestIdentifier:
      kfzAlternateTariffSearchRequestIdentifier,

    inventoryContractTitle: kfzInventoryContractTitle,

    comparisonComponents: {
      HeaderComponent: () =>
        import('@/features/Sparten/shared/components/KfzComparisonHeader.vue'),
    },

    inputMappingGrouping: [
      carGroupMapping,
      versicherungsnehmerGroupMapping,
      absicherungsGroupMapping,
      beitragsreduzierungsGroupMapping,
    ],

    comparison: {
      getDetails: () => {
        const map = new Map<string, ComparisonDetail>();

        map.set('betrag', {
          label: 'zu zahlender Beitrag',
          format: formatCurrency,
        });

        return map;
      },
      serviceFields: [
        {
          label: 'Deckungssumme',
          fieldPath: 'deckungssumme',
          inputFormatter: formatChoice(SIMPLE_CHOICE.KFZ_DECKUNGSSUMME),
          formatter: string,
        },
        {
          label: 'Fahrerschutzversicherung',
          fieldPath: 'fahrerschutzversicherung',
          inputFormatter: boolean,
          formatter: string,
        },
        {
          label: 'Schutzbriefleistungen',
          fieldPath: 'schutzbriefleistungen',
          inputFormatter: boolean,
          formatter: string,
        },
        {
          label: 'Werkstattbindung',
          fieldPath: 'werkstattbindung',
          inputFormatter: formatChoice(SIMPLE_CHOICE.WERKSTATTBINDUNG),
          formatter: string,
          requirement: () => isNotHaftpflicht.value,
        },
        {
          label: 'Tierbissfolgeschäden',
          fieldPath: 'tierbissfolgeschaeden',
          inputFormatter: formatChoice(SIMPLE_CHOICE.TIERBISSFOLGESCHAEDEN),
          formatter: string,
          requirement: () => isNotHaftpflicht.value,
        },
        {
          label: 'Rabattschutz Kfz-Haftpflicht',
          fieldPath: 'rabattschutzKfzHaftpflicht',
          inputFormatter: boolean,
          formatter: string,
          requirement: () => usePkwInputStore().kaskoTyp !== KASKOTYP.VOLLKASKO,
        },
        {
          label: 'Kollision mit Tieren aller Art',
          fieldPath: 'kollisionMitTieren',
          inputFormatter: boolean,
          formatter: string,
          requirement: () => isNotHaftpflicht.value,
        },
        {
          label: 'GAP Deckung',
          fieldPath: 'gapDeckung',
          inputFormatter: boolean,
          formatter: string,
          requirement: () =>
            isNotHaftpflicht.value &&
            getSimpleChoiceMeta(
              SIMPLE_CHOICE.FINANZIERUNGSART,
              SIMPLE_CHOICE_META.FINANZIERUNGSART_NOT_SELF_FINANCING,
            ).includes(usePkwInputStore().fahrzeug.finanzierungsart),
        },
        {
          label: 'Rabattschutz',
          fieldPath: 'rabattschutzVollkasko',
          inputFormatter: boolean,
          formatter: string,
          requirement: () => isVollkasko.value,
        },
        {
          label: 'Selbstbeteiligung Vollkasko',
          fieldPath: 'kaskoSB.vollkasko',
          formatter: (value: string) => formatCurrency(value, true),
          inputFieldAsFallback: true,
          requirement: () => isVollkasko.value,
        },
        {
          label: 'Selbstbeteiligung Teilkasko',
          fieldPath: () =>
            isVollkasko.value && usePkwInputStore().kaskoSB.gleicheSB
              ? 'kaskoSB.vollkasko'
              : 'kaskoSB.teilkasko',
          formatter: (value: string) => formatCurrency(value, true),
          inputFieldAsFallback: true,
          requirement: () => isNotHaftpflicht.value,
        },
        {
          label: 'SF-Klasse Haftpflicht',
          fieldPath: 'sfKlasseKH',
          formatter: formatChoice(SIMPLE_CHOICE.SF_KLASSE_KH),
          inputFormatter: formatChoice(SIMPLE_CHOICE.SF_KLASSE_KH),
          inputFieldAsFallback: false,
        },
        {
          label: 'SF-Klasse Vollkasko',
          fieldPath: 'sfKlasseVK',
          formatter: formatChoice(SIMPLE_CHOICE.SF_KLASSE_VK),
          inputFormatter: formatChoice(SIMPLE_CHOICE.SF_KLASSE_VK),
          inputFieldAsFallback: false,
          requirement: () => isVollkasko.value,
        },
        {
          label: 'Beitrag Haftpflicht',
          fieldPath: 'beitragHaftpflicht',
          formatter: formatCurrency,
          inputFormatter: string,
          inputFieldAsFallback: false,
        },
        {
          label: 'Beitrag Kasko',
          fieldPath: 'beitragKasko',
          formatter: formatCurrency,
          inputFormatter: string,
          inputFieldAsFallback: false,
          requirement: () => isNotHaftpflicht.value,
        },
        {
          label: 'Zahlweise',
          fieldPath: 'zahlweise',
          formatter: formatChoice(SIMPLE_CHOICE.PAYMENT_PERIOD),
        },
      ],
    },

    baseTariffComponent: () =>
      import('@/features/Tariffs/BaseTariff/BaseTariff.vue'),

    tariffComponent: () =>
      import(
        '@/features/Sparten/pkw/components/TariffDetails/TariffDetails.vue'
      ),

    devtools: {
      directAccess: {
        input: {
          icon: 'edit',
          setup: async () => getHelper('pkw').resetState(),
        },
        offers: {
          icon: 'list',
          setup: async () => getHelper('pkw').selectAllFeatures(),
        },
        comparison: {
          icon: 'compare_arrows',
          setup: async () => getHelper('pkw').loadTariffs(),
        },
        contract: {
          icon: 'description',
          setup: async () => getHelper('pkw').showContract(),
        },
      },
    },

    contractVnSchema: () =>
      useClientStore().vnIsCompany
        ? VersicherungsnehmerCompanyPropertyContractSchema
        : VersicherungsnehmerPropertyContractSchema,

    hasPreviousInsurance: kfzPreviousInsurance,

    hasAutomaticTermination: () =>
      getSimpleChoiceMeta(
        SIMPLE_CHOICE.VERSICHERUNGSART_VERSICHERER_TARIF_WECHSEL,
        SIMPLE_CHOICE_META.VERSICHERUNGSART_TARIF_VERSICHERER_WECHSEL,
      ).includes(usePkwInputStore().allgemein.versicherungsart),

    documentTitlePrefix: () =>
      import('@/features/Sparten/shared/kfz/documentTitlePrefix'),

    requiresBankTransferInformation: () =>
      getSimpleChoiceMeta(
        SIMPLE_CHOICE.PAYMENT_METHOD,
        SIMPLE_CHOICE_META.PAYMENT_METHOD_NEEDS_BANK_DETAILS,
      ).includes(usePkwInputStore().zahlart),

    requiresBankTransferPayeeDetails: () => {
      return (
        !isCompany.value ||
        !useClientStore().isSameByRole(
          ROLE.VERSICHERUNGSNEHMER,
          ROLE.ZAHLPERSON,
        )
      );
    },

    enableEVBService: true,
  },

  steps: {
    input: {
      component: () => import('@/features/Sparten/pkw/views/InputView.vue'),
    },
    offers: {
      component: () => import('@/features/Sparten/pkw/views/Tariffs.vue'),
    },
    comparison: {
      component: () =>
        import('@/features/Sparten/shared/views/kfz/Comparison.vue'),
    },
    contract: {
      component: () =>
        import('@/features/Sparten/shared/views/kfz/ContractView.vue'),
    },
    completion: {
      component: () => import('@/views/Layouts/Completion/Completion.vue'),
    },
  },
};
